// The signed-in user, at boot: resolve the auth session (retrying a transient
// failure), then load the user's properties and data into the user store. The
// session identifies them to PostHog. The router guard calls identify() and
// waits on waitForLogin().
import { watch } from "vue"
import type { Principal } from "@auger/identity"
import { general as config } from "@/common/config"
import { fetchUserData } from "@/common/hasura/userdata"
import { fetchSelf } from "@/common/hasura/users"
import auth from "@/identity/auth"
import { client } from "@/init/apollo"
import { useUserStore } from "@/stores/user"

const CurrentUser = (function () {
  // The signed-in person's record, by the session's person id. There is no
  // users row to read (Phase 7 PR C): the person record is the identity.
  function fetchUserProperties() {
    const userStore = useUserStore()
    const principal = userStore.principal
    const personId = principal?.identity.auger_person_id
    if (!personId) {
      console.error("user property fetching error: the session carries no person id")
      return Promise.resolve()
    }
    return client
      .query({ query: fetchSelf, variables: { personId } })
      .then((queryResult) => {
        const person = queryResult.data?.person
        if (!person) return
        const email = person.contacts?.[0]?.value ?? null
        userStore.updateProperties({
          email,
          name: person.name ?? null,
          record_id: person.id,
          record: { ...person },
        })
      })
      .catch((error) => console.error("user property fetching error", error))
  }

  function fetchUserDataProperties() {
    const userStore = useUserStore()
    return client
      .query({ query: fetchUserData })
      .then((queryResult) => {
        const row = queryResult.data?.user_data?.[0]
        userStore.updateUserData(row?.data ? { ...row.data } : {})
        userStore.updateViewSettings(row?.view_settings ? { ...row.view_settings } : {})
      })
      .catch((error) => console.error("user data fetching error", error))
  }

  function replicateStateForUser(userId: string) {
    const userStore = useUserStore()
    userStore.setForcedUser(userId, config.forcedPerson)
    return Promise.all([fetchUserProperties(), fetchUserDataProperties()])
  }

  function updateAuthentication(principal: Principal | null) {
    const userStore = useUserStore()
    userStore.setPrincipal(principal)
    if (!principal) return
    return Promise.all([fetchUserProperties(), fetchUserDataProperties()])
  }

  // Retry resolveSession with backoff before giving up. Auth-level failures
  // (login_required, etc.) never land here — the facade's getIdToken already
  // redirects to the sign-in page and returns a never-resolving promise on
  // those. What reaches this catch is transient/unexpected failures (e.g. a
  // network blip during boot); without a retry, one of those permanently
  // wedges waitForLogin() and the app is a blank page.
  const RETRY_DELAYS_MS = [1000, 2000, 4000]
  function resolveSessionWithRetry(attempt = 0): Promise<Principal | null> {
    return auth.resolveSession().catch((err) => {
      if (attempt >= RETRY_DELAYS_MS.length) {
        console.error("auth session resolution failed after retries", err)
        auth.login(window.location.pathname + window.location.search + window.location.hash)
        return new Promise<Principal | null>(() => {}) // never resolves; navigating away
      }
      return new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt])).then(() =>
        resolveSessionWithRetry(attempt + 1)
      )
    })
  }

  // identify() runs once per page load. The router guard calls it on the
  // first navigation to an Auger route, so the /identity/* pages (which serve
  // people with no Auger account) never trigger a store load, and a login
  // that finishes on /identity/callback and continues into the app in the
  // same page load still gets the user resolved.
  let identified = false

  return {
    identify() {
      if (identified) return
      identified = true
      if (auth.enabled) {
        resolveSessionWithRetry().then(updateAuthentication)
      } else if (config.forcedUser) {
        replicateStateForUser(config.forcedUser)
      }
    },

    waitForLogin() {
      return new Promise<void>((resolve) => {
        const userStore = useUserStore()
        // Resolve if logged in with userData loaded, or if logging out (the
        // guard will then re-check auth state and redirect to login).
        // "Loaded" is the person record being read: an email is not required,
        // since a person record may carry none.
        const ready = () =>
          userStore.loggingOut || (userStore.user.record_id != null && userStore.userData !== null)
        if (ready()) return resolve()
        const stop = watch(
          () => [userStore.user.record_id, userStore.userData, userStore.loggingOut],
          () => {
            if (ready()) {
              stop()
              resolve()
            }
          }
        )
      })
    },
  }
})()

export default CurrentUser
